import React from 'react';
import { AppInsightsContext, ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ITelemetryItem, ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import webStorage, { LOCAL_STORAGE_KEYS } from '@/core/web-storage/webStorage';
import { BE_ENVIRONMENT_MAPPER } from '@/core/utils/app.constants';

function ApplicationInsightsProvider({ children }: { children: React.ReactNode }) {
  if (ENVIRONMENT_NAME === 'local') {
    return <>{children}</>;
  }

  const reactPluginAi = new ReactPlugin();
  const clickPluginAi = new ClickAnalyticsPlugin();

  const appInsights = new ApplicationInsights({
    config: {
      connectionString: APP_INSIGHTS_CONNECTION_STRING,
      extensions: [reactPluginAi, clickPluginAi],
      extensionConfig: {
        [clickPluginAi.identifier]: {
          autoCapture: true,
          dataTags: {
            useDefaultContentNameOrId: true,
            parentDataTag: 'aiparentdatatag',
          },
        },
      },
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      disableFetchTracking: true,
      disableTelemetry: ENVIRONMENT_NAME === 'develop',
      disableXhr: true,
      enableAjaxErrorStatusText: true,
      enableUnhandledPromiseRejectionTracking: true,
    },
  });

  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer((env: ITelemetryItem) => {
    env.data = env.data || {};
    env.data['Environment'] = BE_ENVIRONMENT_MAPPER[ENVIRONMENT_NAME] ?? 'UNKNOWN';
    env.data['UserId'] = webStorage.local.getItem(LOCAL_STORAGE_KEYS.USER_ID);
    env.data['UserBoard'] = webStorage.local.getItem(LOCAL_STORAGE_KEYS.BOARD_ID);
    env.data['UserPortalRole'] = webStorage.local.getItem(LOCAL_STORAGE_KEYS.PORTAL_ROLE);
    env.data['UserName'] = webStorage.local.getItem(LOCAL_STORAGE_KEYS.USER_NAME);
    env.data['UserEmail'] = webStorage.local.getItem(LOCAL_STORAGE_KEYS.USER_EMAIL);
  });

  return (
    <AppInsightsContext.Provider value={reactPluginAi}>{children}</AppInsightsContext.Provider>
  );
}

export default ApplicationInsightsProvider;
